import React from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { getTextLength } from '../../utils/textUtils';

interface TextStoryViewProps {
  content: string;
  textBackground: string;
  onContentChange: (content: string) => void;
  onBackgroundChange: (background: string) => void;
}

const backgroundOptions = [
  "bg-gradient-to-br from-purple-500 to-pink-500",
  "bg-gradient-to-br from-blue-500 to-purple-500",
  "bg-gradient-to-br from-green-500 to-blue-500",
  "bg-gradient-to-br from-yellow-500 to-red-500",
  "bg-gradient-to-br from-pink-500 to-yellow-500",
  "bg-gradient-to-br from-indigo-500 to-purple-500",
  "bg-black",
  "bg-white"
];

const TextStoryView: React.FC<TextStoryViewProps> = ({
  content,
  textBackground,
  onContentChange,
  onBackgroundChange,
}) => {
  const { t } = useTranslation();
  return (
    <div className={`relative w-full h-full ${textBackground} flex items-center justify-center p-8`}>
      <div className="w-full text-center relative">
        <textarea
          value={content}
          onChange={(e) => {
            const newValue = e.target.value;
            onContentChange(newValue);
          }}
          onInput={(e) => {
            const target = e.target as HTMLTextAreaElement;
            onContentChange(target.value);
          }}
          placeholder={t("story.storyTextPlaceholder")}
          className={`w-full bg-transparent text-center text-xl font-bold resize-none focus:outline-none ${
            content ? 'border-none' : 'border-2 border-dashed border-opacity-50 rounded-lg p-4'
          }`}
          rows={8}
          maxLength={300}
          style={{
            color: textBackground.includes('white') ? '#000000' : '#ffffff',
            borderColor: !content && textBackground.includes('white') ? '#000000' : '#ffffff',
            fontFamily: 'Vazirmatn, Tahoma, Arial Unicode MS, sans-serif',
            direction: 'rtl',
            lineHeight: '1.5',
            whiteSpace: 'pre-wrap',
            wordWrap: 'break-word'
          }}
        />
      </div>

      {/* Background Options */}
      <div className="absolute w-full bottom-20 left-4">
        <div className="flex justify-center gap-2">
          {backgroundOptions.map((bg, index) => (
            <button
              key={index}
              onClick={() => onBackgroundChange(bg)}
              className={`w-8 h-8 rounded-full ${bg} border-2 transition-all ${
                textBackground === bg ? 'border-white scale-110' : 'border-transparent'
              }`}
            />
          ))}
        </div>
      </div>

      {/* Character count */}
      <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2">
        <div
          className="px-3 py-1 rounded-full text-sm backdrop-blur-md"
          style={{
            backgroundColor: textBackground.includes('white') ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.3)',
            color: textBackground.includes('white') ? '#000000' : '#ffffff'
          }}
        >
          {getTextLength(content)}/300
        </div>
      </div>
    </div>
  );
};

export default TextStoryView;

